export const ATTRIBUTION_COOKIE_NAME = "adp_attr_v1" as const;
const SCHEMA_VERSION = 1;

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  referrer?: string;
  landingPage?: string;
}

const UTM_KEYS: ReadonlyArray<[keyof Attribution, string]> = [
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmTerm", "utm_term"],
  ["utmContent", "utm_content"],
  ["gclid", "gclid"],
  ["fbclid", "fbclid"],
  ["ttclid", "ttclid"],
  ["msclkid", "msclkid"],
];

/**
 * Read UTM params + click-IDs + landing page from a URL. Returns null when the
 * visit carries no attribution signals at all (neither UTM, click-IDs, nor
 * an external referrer).
 */
export function extractAttributionFromUrl(input: {
  url: URL;
  referrer: string;
}): Attribution | null {
  const { url, referrer } = input;
  const attr: Attribution = {};
  for (const [field, param] of UTM_KEYS) {
    const value = url.searchParams.get(param);
    if (value) {
      attr[field] = value;
    }
  }
  if (referrer && !referrer.startsWith(url.origin)) {
    attr.referrer = referrer;
  }
  attr.landingPage = url.pathname;

  const hasSignal =
    UTM_KEYS.some(([field]) => attr[field] !== undefined) || attr.referrer !== undefined;
  return hasSignal ? attr : null;
}

/**
 * Parses the attribution cookie value. Defensive: null on malformed, missing
 * version, or wrong version (so a stale cookie from a future schema migration
 * is treated as no cookie).
 */
export function parseAttributionCookie(value: string | undefined): Attribution | null {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      (parsed as { v?: unknown }).v !== SCHEMA_VERSION
    ) {
      return null;
    }
    const { v: _v, ...attr } = parsed as Attribution & { v: number };
    return attr;
  } catch {
    return null;
  }
}

/**
 * Build a Set-Cookie attribute string. Value is URL-encoded JSON with a `v`
 * schema marker. 30-day Max-Age matches typical attribution windows.
 */
export function serializeAttributionCookie(attr: Attribution, opts: { secure: boolean }): string {
  const value = encodeURIComponent(JSON.stringify({ v: SCHEMA_VERSION, ...attr }));
  const parts = [
    `${ATTRIBUTION_COOKIE_NAME}=${value}`,
    "Path=/",
    "Max-Age=2592000",
    "SameSite=Lax",
  ];
  if (opts.secure) {
    parts.push("Secure");
  }
  return parts.join("; ");
}

/**
 * Returns an HTML <script> tag (with nonce) that captures attribution on page
 * load. Mirrors extractAttributionFromUrl + serializeAttributionCookie in
 * vanilla JS so it runs without any client bundle. First-touch: aborts when
 * adp_attr_v1 already exists.
 *
 * Emitted by AnalyticsInjector alongside the dataLayer init, but ONLY when
 * trackers are also being rendered (consent gate is upstream).
 */
export function renderAttributionCaptureScript(nonce: string, opts: { secure: boolean }): string {
  const secureFlag = opts.secure ? "; Secure" : "";
  return `<script nonce="${nonce}">(function(){try{if(document.cookie.indexOf('adp_attr_v1=')!==-1)return;var p=new URLSearchParams(location.search);var k=[['utmSource','utm_source'],['utmMedium','utm_medium'],['utmCampaign','utm_campaign'],['utmTerm','utm_term'],['utmContent','utm_content'],['gclid','gclid'],['fbclid','fbclid'],['ttclid','ttclid'],['msclkid','msclkid']];var a={v:1};var has=false;for(var i=0;i<k.length;i++){var v=p.get(k[i][1]);if(v){a[k[i][0]]=v;has=true;}}var ref=document.referrer;if(ref&&ref.indexOf(location.origin)!==0){a.referrer=ref;has=true;}a.landingPage=location.pathname;if(!has)return;document.cookie='adp_attr_v1='+encodeURIComponent(JSON.stringify(a))+'; Path=/; Max-Age=2592000; SameSite=Lax${secureFlag}';}catch(e){}})();</script>`;
}
